import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import useNoIndex from "../hooks/useNoIndex";
import { STATIC_MEDIA } from "../content/media";
import { createMediaItem, deleteMediaItem, fetchAdminMedia, updateMediaItem } from "../lib/api";
import { PostBody, formatMediaDate } from "../lib/media";
import { translations } from "../i18n/translations";

const TOKEN_KEY = "mise-admin-token";
const TAGS = translations.en.media.tags;
const today = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD

const EMPTY = {
  kind: "press",
  title: "",
  title_fr: "",
  date: today(),
  summary: "",
  summary_fr: "",
  outlet: "",
  author: "",
  format: "article",
  url: "",
  body: "",
  body_fr: "",
  cta_label: "",
  cta_label_fr: "",
  image: "",
  image_alt: "",
  tag: "company",
  published: true,
};

const inputCls =
  "w-full bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30";

const Field = ({ label, hint, children }) => (
  <label className="grid gap-2">
    <span className="text-sm text-white/60">{label}</span>
    {children}
    {hint && <span className="text-xs text-white/40 leading-relaxed">{hint}</span>}
  </label>
);

const readToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

const AdminMedia = () => {
  useNoIndex();
  const [token, setToken] = useState(readToken);
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showFr, setShowFr] = useState(false);
  const [preview, setPreview] = useState(false);

  const lock = useCallback(() => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {}
    setAuthed(false);
    setToken("");
  }, []);

  const handleError = useCallback(
    (err, fallback) => {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Invalid admin token");
        lock();
      } else {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : fallback);
      }
    },
    [lock],
  );

  const load = useCallback(
    async (tok) => {
      try {
        setItems(await fetchAdminMedia(tok));
        return true;
      } catch (err) {
        handleError(err, "Could not load media items");
        return false;
      }
    },
    [handleError],
  );

  const unlock = async (e) => {
    e?.preventDefault();
    const tok = token.trim();
    if (!tok) return toast.error("Enter the admin token");
    if (await load(tok)) {
      try {
        sessionStorage.setItem(TOKEN_KEY, tok);
      } catch {}
      setAuthed(true);
    }
    return undefined;
  };

  // Unlock straight away if this tab already has a token from another admin page.
  useEffect(() => {
    if (token) unlock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const reset = () => {
    setForm({ ...EMPTY, date: today() });
    setEditingId(null);
    setPreview(false);
  };

  const startEdit = (item) => {
    const next = { ...EMPTY };
    Object.keys(EMPTY).forEach((k) => {
      if (item[k] !== undefined && item[k] !== null) next[k] = item[k];
    });
    setForm(next);
    setEditingId(item.id);
    setShowFr(Boolean(item.title_fr || item.summary_fr || item.body_fr));
    setPreview(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    try {
      if (editingId) {
        await updateMediaItem(token.trim(), editingId, payload);
        toast.success("Saved");
      } else {
        await createMediaItem(token.trim(), payload);
        toast.success(form.published ? "Added to the Media page" : "Saved as a draft");
      }
      reset();
      await load(token.trim());
    } catch (err) {
      handleError(err, "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    try {
      await deleteMediaItem(token.trim(), item.id);
      toast.success("Deleted");
      if (editingId === item.id) reset();
      await load(token.trim());
    } catch (err) {
      handleError(err, "Could not delete");
    }
  };

  const togglePublished = async (item) => {
    const payload = { ...EMPTY };
    Object.keys(EMPTY).forEach((k) => {
      if (item[k] !== undefined && item[k] !== null) payload[k] = item[k];
    });
    payload.published = !item.published;
    try {
      await updateMediaItem(token.trim(), item.id, payload);
      await load(token.trim());
    } catch (err) {
      handleError(err, "Could not update");
    }
  };

  const isPress = form.kind === "press";

  if (!authed) {
    return (
      <div className="mise-container py-20 md:py-24 max-w-2xl" data-testid="page-admin-media">
        <p className="mise-eyebrow">Admin · Media</p>
        <h1 className="mt-3 text-4xl md:text-5xl font-semibold tracking-tight text-white">Media page</h1>
        <form onSubmit={unlock} className="mise-card p-8 mt-10 hover:!translate-y-0" data-testid="admin-unlock-form">
          <label className="text-sm text-white/60" htmlFor="admin-token">Admin token</label>
          <input
            id="admin-token"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className={`${inputCls} mt-2`}
            placeholder="Enter admin token"
            data-testid="admin-token-input"
            autoFocus
          />
          <button type="submit" className="mise-pill-primary mt-6" data-testid="admin-unlock-submit">Unlock</button>
        </form>
      </div>
    );
  }

  return (
    <div className="mise-container py-16 md:py-20 max-w-5xl" data-testid="page-admin-media">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mise-eyebrow">Admin · Media</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-semibold tracking-tight text-white">Media page</h1>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/media" target="_blank" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white">
            View page <ExternalLink size={14} />
          </Link>
          <button type="button" onClick={lock} className="text-white/60 hover:text-white" data-testid="admin-lock-btn">Lock</button>
        </div>
      </div>
      <p className="mt-4 max-w-3xl text-white/70 leading-relaxed">
        Add press coverage (someone else's story about Mise) or a post (our own news, with its own page). Everything
        here shows on /media alongside the items built into the site, newest first.
      </p>

      {/* EDITOR */}
      <form onSubmit={save} className="mise-card hover:!translate-y-0 p-6 md:p-8 mt-10 space-y-6" data-testid="admin-media-form">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-lg font-medium">{editingId ? "Edit item" : "Add an item"}</p>
          <div className="inline-flex rounded-full border border-[#444444] p-1 text-sm">
            {[
              ["press", "Press coverage"],
              ["post", "Mise post"],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setForm((f) => ({ ...f, kind: k }))}
                className={`rounded-full px-4 py-1.5 ${form.kind === k ? "bg-[#E8490F] text-white" : "text-white/70 hover:text-white"}`}
                data-testid={`admin-media-kind-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {isPress && (
          <div className="grid gap-5 md:grid-cols-3">
            <Field label="Outlet">
              <input className={inputCls} value={form.outlet} onChange={set("outlet")} placeholder="CTV News Calgary" required />
            </Field>
            <Field label="Reporter (optional)">
              <input className={inputCls} value={form.author} onChange={set("author")} />
            </Field>
            <Field label="Format">
              <select className={inputCls} value={form.format} onChange={set("format")}>
                <option value="article">Article</option>
                <option value="video">Video / TV</option>
                <option value="podcast">Podcast</option>
                <option value="radio">Radio</option>
              </select>
            </Field>
          </div>
        )}

        <Field
          label={isPress ? "Headline" : "Title"}
          hint={isPress ? "Their headline, word for word." : "A plain statement of what happened, under about 70 characters."}
        >
          <input className={inputCls} value={form.title} onChange={set("title")} required maxLength={200} data-testid="admin-media-title" />
        </Field>

        <div className="grid gap-5 md:grid-cols-3">
          <Field label="Date">
            <input type="date" className={inputCls} value={form.date} onChange={set("date")} required />
          </Field>
          <Field label="Part of Mise">
            <select className={inputCls} value={form.tag} onChange={set("tag")}>
              {Object.entries(TAGS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <label className="flex h-[50px] items-center gap-3 text-white/80">
              <input type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4 accent-[#E8490F]" />
              Published (untick to keep as a draft)
            </label>
          </Field>
        </div>

        <Field
          label="Summary"
          hint={`${form.summary.length}/300. One or two sentences in our own words, shown on the card${isPress ? "" : " and under the title"}.`}
        >
          <textarea className={`${inputCls} min-h-[90px]`} value={form.summary} onChange={set("summary")} required maxLength={400} />
        </Field>

        {isPress ? (
          <Field label="Link to the story">
            <input type="url" className={inputCls} value={form.url} onChange={set("url")} placeholder="https://" required />
          </Field>
        ) : (
          <>
            <Field
              label="Body"
              hint={'Blank line between paragraphs. "## " starts a subheading, "- " a bullet, "> " a pull quote (add "> — Name" on the next line to credit it).'}
            >
              <textarea className={`${inputCls} min-h-[320px] font-mono text-sm`} value={form.body} onChange={set("body")} required />
            </Field>
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Button label (optional)" hint="e.g. Join the network">
                <input className={inputCls} value={form.cta_label} onChange={set("cta_label")} />
              </Field>
              <Field label="Button link (optional)" hint="https://… or a page on this site like /contact">
                <input className={inputCls} value={form.url} onChange={set("url")} />
              </Field>
              <Field label="Image (optional)" hint="Landscape, roughly 16:9. A full https:// link or a file in /images/.">
                <input className={inputCls} value={form.image} onChange={set("image")} />
              </Field>
              <Field label="Image description" hint="What's in the photo, for screen readers.">
                <input className={inputCls} value={form.image_alt} onChange={set("image_alt")} />
              </Field>
            </div>
          </>
        )}

        <div>
          <button type="button" onClick={() => setShowFr((v) => !v)} className="text-sm text-[#E8490F] hover:underline">
            {showFr ? "Hide French" : "Add French (optional)"}
          </button>
          {showFr && (
            <div className="mt-5 grid gap-5 rounded-2xl border border-[#444444] p-5">
              <p className="text-xs text-white/50">Empty fields show the English on the French site.</p>
              {!isPress && (
                <Field label="Title (FR)">
                  <input className={inputCls} value={form.title_fr} onChange={set("title_fr")} />
                </Field>
              )}
              <Field label="Summary (FR)">
                <textarea className={`${inputCls} min-h-[80px]`} value={form.summary_fr} onChange={set("summary_fr")} maxLength={400} />
              </Field>
              {!isPress && (
                <>
                  <Field label="Body (FR)">
                    <textarea className={`${inputCls} min-h-[220px] font-mono text-sm`} value={form.body_fr} onChange={set("body_fr")} />
                  </Field>
                  <Field label="Button label (FR)">
                    <input className={inputCls} value={form.cta_label_fr} onChange={set("cta_label_fr")} />
                  </Field>
                </>
              )}
            </div>
          )}
        </div>

        {!isPress && form.body && (
          <div>
            <button type="button" onClick={() => setPreview((v) => !v)} className="text-sm text-[#E8490F] hover:underline">
              {preview ? "Hide preview" : "Preview the body"}
            </button>
            {preview && (
              <div className="mt-5 rounded-2xl border border-[#444444] bg-[#111111] p-6">
                <PostBody text={form.body} />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button type="submit" disabled={saving} className="mise-pill-primary" data-testid="admin-media-save">
            {saving ? "Saving…" : editingId ? "Save changes" : form.published ? "Publish" : "Save draft"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="mise-pill-secondary">Cancel</button>
          )}
        </div>
      </form>

      {/* LIST */}
      <h2 className="mt-14 text-2xl font-semibold tracking-tight">Added here</h2>
      {items.length === 0 ? (
        <p className="mt-4 text-white/50">Nothing yet.</p>
      ) : (
        <ul className="mt-5 divide-y divide-[#2a2a2a] rounded-2xl border border-[#444444]" data-testid="admin-media-list">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-4 p-5">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-white/45">
                  {item.kind === "press" ? item.outlet : "Mise post"} · {formatMediaDate(item.date, "en")}
                  {!item.published && <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-white/70">Draft</span>}
                </p>
                <p className="mt-1 truncate text-white">{item.title}</p>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {item.kind === "post" && item.published && (
                  <Link to={`/media/${item.slug}`} target="_blank" className="rounded-full border border-[#444444] px-3 py-1.5 text-white/70 hover:text-white">
                    View
                  </Link>
                )}
                <button type="button" onClick={() => togglePublished(item)} className="rounded-full border border-[#444444] px-3 py-1.5 text-white/70 hover:text-white">
                  {item.published ? "Unpublish" : "Publish"}
                </button>
                <button type="button" onClick={() => startEdit(item)} className="rounded-full border border-[#444444] p-2 text-white/70 hover:text-white" aria-label="Edit">
                  <Pencil size={15} />
                </button>
                <button type="button" onClick={() => remove(item)} className="rounded-full border border-[#444444] p-2 text-white/70 hover:text-red-400" aria-label="Delete">
                  <Trash2 size={15} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h2 className="mt-12 text-2xl font-semibold tracking-tight">Built into the site</h2>
      <p className="mt-2 text-sm text-white/50">These live in the code (frontend/src/content/media.js). Ask Claude to change them.</p>
      <ul className="mt-5 divide-y divide-[#2a2a2a] rounded-2xl border border-[#2a2a2a] text-white/60">
        {STATIC_MEDIA.map((item) => (
          <li key={item.id} className="p-5">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">
              {item.kind === "press" ? item.outlet : "Mise post"} · {formatMediaDate(item.date, "en")}
            </p>
            <p className="mt-1 truncate">{item.title}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AdminMedia;
